import { useCallback } from 'react';

export interface UseSelectionProps {
  selectedIds: Set<string>;
  onSelect: (id: string) => void;
  onDeselect: (id: string) => void;
  onSetSelected: (ids: string[]) => void;
  onClearSelection: () => void;
  allVisibleIds: string[];
}

export interface UseSelectionReturn {
  toggleSelect: (id: string) => void;
  selectRange: (startId: string, endId: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  handleItemClick: (
    id: string,
    event: React.MouseEvent,
    lastSelectedId?: string
  ) => string; // returns new lastSelectedId
}

/**
 * Hook for managing multi-select functionality
 * Supports:
 * - Single click to select
 * - Cmd/Ctrl + click to toggle
 * - Shift + click for range selection
 * - Select all / clear all
 */
export function useSelection({
  selectedIds,
  onSelect,
  onDeselect,
  onSetSelected,
  onClearSelection,
  allVisibleIds,
}: UseSelectionProps): UseSelectionReturn {
  const toggleSelect = useCallback(
    (id: string) => {
      if (selectedIds.has(id)) {
        onDeselect(id);
      } else {
        onSelect(id);
      }
    },
    [selectedIds, onSelect, onDeselect]
  );

  const selectRange = useCallback(
    (startId: string, endId: string) => {
      const startIndex = allVisibleIds.indexOf(startId);
      const endIndex = allVisibleIds.indexOf(endId);

      if (startIndex === -1 || endIndex === -1) return;

      const [from, to] = startIndex < endIndex
        ? [startIndex, endIndex]
        : [endIndex, startIndex];

      const rangeIds = allVisibleIds.slice(from, to + 1);

      // Add range to existing selection
      const newSelection = new Set(selectedIds);
      rangeIds.forEach(id => newSelection.add(id));

      onSetSelected(Array.from(newSelection));
    },
    [allVisibleIds, selectedIds, onSetSelected]
  );

  const selectAll = useCallback(() => {
    onSetSelected(allVisibleIds);
  }, [allVisibleIds, onSetSelected]);

  const clearSelection = useCallback(() => {
    onClearSelection();
  }, [onClearSelection]);

  /**
   * Handle click on an item with modifier keys
   * Returns the new lastSelectedId for range selection tracking
   */
  const handleItemClick = useCallback(
    (id: string, event: React.MouseEvent, lastSelectedId?: string): string => {
      const isMeta = event.metaKey || event.ctrlKey; // Cmd on Mac, Ctrl on Windows
      const isShift = event.shiftKey;

      if (isShift && lastSelectedId) {
        // Range select from lastSelected to current
        event.preventDefault();
        selectRange(lastSelectedId, id);
        return lastSelectedId; // Keep the anchor
      } else if (isMeta) {
        // Toggle individual selection
        event.preventDefault();
        toggleSelect(id);
        return id;
      } else {
        // Regular click - select only this item
        if (selectedIds.size > 1 || !selectedIds.has(id)) {
          onSetSelected([id]);
        }
        return id;
      }
    },
    [selectedIds, toggleSelect, selectRange, onSetSelected]
  );

  return {
    toggleSelect,
    selectRange,
    selectAll,
    clearSelection,
    handleItemClick,
  };
}
